import { z } from 'zod';
import apiClient from '@shared/api/http';
import { toItemArray, toSnakeCaseKeys } from '@shared/api/keys';
import type {
  Rack,
  RackDetail,
  RackDevice,
  RackId,
  RackSummary,
  RackWritePayload,
} from '../domain/rack';

const RackSummaryDtoSchema = z.object({
  id: z.string(),
  room_id: z.string(),
  code: z.string(),
  name: z.string(),
  status: z.enum(['active', 'inactive']),
  size: z.string().nullable(),
  serial_number: z.string().nullable(),
});

const RackLocationDtoSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
});

const RackDetailDtoSchema = RackSummaryDtoSchema.extend({
  room: RackLocationDtoSchema,
  datacenter: RackLocationDtoSchema,
});

const RackMutationDtoSchema = z.object({
  id: z.string(),
  room_id: z.string(),
  name: z.string(),
  status: z.string().optional(),
  units: z.number().nullable().optional(),
  serial: z.string().nullable().optional(),
  size: z.string().nullable().optional(),
  serial_number: z.string().nullable().optional(),
});

const RackDeviceDtoSchema = z.object({
  id: z.string().optional(),
  device_model: z.string().optional(),
  brand: z.string().optional(),
  deployed_at: z.string().optional(),
});

function toRackSummary(dto: z.infer<typeof RackSummaryDtoSchema>): RackSummary {
  return {
    id: dto.id as RackId,
    name: dto.name,
    salaId: dto.room_id as RackSummary['salaId'],
    status: dto.status,
    size: dto.size,
    serial: dto.serial_number,
  };
}

function toRackDetail(dto: z.infer<typeof RackDetailDtoSchema>): RackDetail {
  return {
    ...toRackSummary(dto),
    salaName: dto.room.name,
    datacenterName: dto.datacenter.name,
    datacenterId: dto.datacenter.id,
  };
}

function toMutationDomain(dto: z.infer<typeof RackMutationDtoSchema>): Rack {
  return {
    id: dto.id as RackId,
    name: dto.name,
    salaId: dto.room_id as Rack['salaId'],
    status: dto.status,
    units: dto.units,
    size: dto.size,
    serial: dto.serial_number ?? dto.serial,
  };
}

function toDeviceDomain(dto: z.infer<typeof RackDeviceDtoSchema>): RackDevice {
  return {
    id: dto.id,
    deviceModel: dto.device_model,
    brand: dto.brand,
    deployedAt: dto.deployed_at,
  };
}

function toDto(payload: RackWritePayload) {
  return {
    roomId: payload.salaId,
    name: payload.name,
    units: payload.units,
    serial: payload.serial,
  };
}

export const rackRepository = {
  async getAll(): Promise<RackSummary[]> {
    const { data } = await apiClient.get('/racks');
    return z
      .array(RackSummaryDtoSchema)
      .parse(toSnakeCaseKeys(toItemArray(data)))
      .map(toRackSummary);
  },

  async getById(id: RackId): Promise<RackDetail | null> {
    const { data } = await apiClient.get(`/racks/${id}`);
    if (!data) return null;
    return toRackDetail(RackDetailDtoSchema.parse(toSnakeCaseKeys(data)));
  },

  async getDevices(id: RackId): Promise<RackDevice[]> {
    const { data } = await apiClient.get(`/racks/${id}/device-instances`);
    return z
      .array(RackDeviceDtoSchema)
      .parse(toSnakeCaseKeys(toItemArray(data)))
      .map(toDeviceDomain);
  },

  async create(payload: RackWritePayload): Promise<Rack> {
    const { data } = await apiClient.post('/racks', toDto(payload));
    return toMutationDomain(RackMutationDtoSchema.parse(toSnakeCaseKeys(data)));
  },

  async update(id: RackId, payload: RackWritePayload): Promise<Rack> {
    const { data } = await apiClient.patch(`/racks/${id}`, toDto(payload));
    return toMutationDomain(RackMutationDtoSchema.parse(toSnakeCaseKeys(data)));
  },

  // Deja pasar el error del backend tal cual (spec.md UC-6: sin chequeo previo de
  // dispositivos montados en frontend) — el mapeo a mensaje legible ocurre en application/.
  async remove(id: RackId): Promise<void> {
    await apiClient.delete(`/racks/${id}`);
  },
};
